import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ApiService } from 'src/app/services/api.service';
import { NotificationService } from 'src/app/services/notification.service';
import { TranslateKey } from 'src/app/services/translate.service';
import { isInputAnEmail } from 'src/app/utils/validator';
import {
	CreateUserExploitationRequest,
	ExploitationType,
	FeatureCommandeBoucles,
	FeatureDisplayAlertes,
	FeatureDisplayEquarrissage,
	FeatureSaisiePesee,
	FeatureSaisieReproduction,
	FeatureSaisieSanitaire,
	FeatureUpdateLots,
	FlagMouvement,
	GetExploitationResponse,
	GetUserResponse,
	NiveauAcces,
	OffreAbonnement,
} from '../../services/dtos';
import { caseInsensitiveInclude } from '../../utils/query';

interface Permission {
	id: string;
	translateKey: TranslateKey;
}

interface IdentifiantsEdeFtp {
	utilisateur: string;
	motDePasse: string;
}

interface ProprietesExploitation {
	mouvement: FlagMouvement;
	offreAbonnement: OffreAbonnement;
	featureDisplayAlertes: FeatureDisplayAlertes;
	featureDisplayEquarrissage: FeatureDisplayEquarrissage;
	featureCommandeBoucles: FeatureCommandeBoucles;
	featureUpdateLots: FeatureUpdateLots;
	featureSaisiePesee: FeatureSaisiePesee;
	featureSaisieReproduction: FeatureSaisieReproduction;
	featureSaisieSanitaire: FeatureSaisieSanitaire;
	type: ExploitationType;
	identifiantsEdeFtp: IdentifiantsEdeFtp;
}

interface AutoCompleteEvent {
	query: string;
	originalEvent: PointerEvent | InputEvent;
}

type ValidationError = 'emptyField';

const FTP_CHECK_COOLDOWN_MS = 5000;

@Component({
	templateUrl: './exploitation-page.component.html',
	providers: [ConfirmationService, NotificationService],
	styleUrls: ['./exploitation-page.component.scss'],
})
export class ExploitationPageComponent implements OnInit, OnDestroy {
	permissions: Permission[] = [
		{ id: 'type', translateKey: 'permission.type' },
		{ id: 'offreAbonnement', translateKey: 'permission.offreAbonnement' },
		{ id: 'featureDisplayAlertes', translateKey: 'permission.featureDisplayAlertes' },
		{ id: 'featureDisplayEquarrissage', translateKey: 'permission.featureDisplayEquarrissage' },
		{ id: 'featureCommandeBoucles', translateKey: 'permission.featureCommandeBoucles' },
		{ id: 'featureDisplayTaches', translateKey: 'permission.featureDisplayTaches' },
		{ id: 'featureSaisieIpe', translateKey: 'permission.featureSaisieIpe' },
		{ id: 'featureSaisiePesee', translateKey: 'permission.featureSaisiePesee' },
		{ id: 'featureSaisieReproduction', translateKey: 'permission.featureSaisieReproduction' },
		{ id: 'featureSaisieSanitaire', translateKey: 'permission.featureSaisieSanitaire' },
		{ id: 'featureUpdateLots', translateKey: 'permission.featureUpdateLots' },
		{ id: 'featureCommandeIA', translateKey: 'permission.featureCommandeIA' },
		{ id: 'featureChatBot', translateKey: 'permission.featureChatBot' },
		{ id: 'mouvement', translateKey: 'permission.mouvement' },
	];

	errorUserId: ValidationError | null = null;
	errorAccess: ValidationError | null = null;

	exploitation: GetExploitationResponse;
	proprietesExploitation: ProprietesExploitation;

	currentUser: GetUserResponse;
	currentUserExploitationId: string;
	currentNiveauAcces: string;

	users: GetUserResponse[];
	userEmails: string[] = [];
	filteredEmails: string[] = [];

	permissionEditionDialog: boolean;
	addUserAccessToExploitationDialog: boolean;
	editUserAccessToExploitationDialog: boolean;
	editFtpDialog: boolean;
	isUserListLoading: boolean;
	showPassword = false;

	ftpCheckStatus: 'idle' | 'checking' | 'valid' | 'invalid' = 'idle';
	ftpCheckCooldown = false;
	private ftpCheckCooldownTimeout: ReturnType<typeof setTimeout>;

	newUserAccess: CreateUserExploitationRequest;

	niveauAcces: NiveauAcces;

	selectedProperty: string;

	listeUsersDuCheptel: GetUserResponse[] = [];
	listeUsersNiveauAccesDuCheptel: string[] = [];
	listeUserExploitationIds: string[] = [];

	syncExploitationState: boolean;
	syncAnimauxState: boolean;

	constructor(
		private apiService: ApiService,
		private route: ActivatedRoute,
		private confirmationService: ConfirmationService,
		private notificationService: NotificationService,
	) {}

	ngOnInit() {
		this.apiService.exploitation_getAdminExploitation(this.route.snapshot.paramMap.get('id')).subscribe({
			next: (exploitation) => {
				this.exploitation = exploitation;
				this.proprietesExploitation = {
					mouvement: this.exploitation.mouvement,
					offreAbonnement: this.exploitation.offreAbonnement,
					featureDisplayAlertes: this.exploitation.featureDisplayAlertes,
					featureDisplayEquarrissage: this.exploitation.featureDisplayEquarrissage,
					featureCommandeBoucles: this.exploitation.featureCommandeBoucles,
					featureUpdateLots: this.exploitation.featureUpdateLots,
					featureSaisiePesee: this.exploitation.featureSaisiePesee,
					featureSaisieReproduction: this.exploitation.featureSaisieReproduction,
					featureSaisieSanitaire: this.exploitation.featureSaisieSanitaire,
					type: this.exploitation.type,
					identifiantsEdeFtp: {
						utilisateur: '',
						motDePasse: '',
					},
				};
				this.fetchUserList(exploitation);
				this.fetchFtpCredentials();
			},
			error: () => {
				// L'erreur est déjà gérée par l'intercepteur HTTP
				// On ne fait rien ici pour éviter que l'UI reste bloquée
			},
		});
	}

	ngOnDestroy() {
		clearTimeout(this.ftpCheckCooldownTimeout);
	}

	filterEmails(event: AutoCompleteEvent) {
		if (event.query.length >= 3) {
			this.filteredEmails = this.userEmails.filter((email) => caseInsensitiveInclude(email, event.query));
		} else if ('pointerId' in event.originalEvent) {
			this.filteredEmails = this.userEmails;
		} else {
			this.filteredEmails = [];
		}
	}

	openNewUserAccess() {
		this.errorUserId = null;
		this.errorAccess = null;
		this.newUserAccess = {
			userId: '',
			numeroCheptel: this.exploitation.numeroCheptel,
			niveauAcces: this.niveauAcces,
		};
		this.addUserAccessToExploitationDialog = true;
	}

	private validateAddUserToCheptel() {
		const validateUserId = this.validateUserId();
		const validateAccessType = this.validateAccessType();
		return validateUserId && validateAccessType;
	}

	private validateUserId() {
		const validateUserId = this.newUserAccess.userId !== '' && this.newUserAccess.userId !== undefined;
		if (!validateUserId) {
			this.errorUserId = 'emptyField';
			return false;
		}
		return true;
	}

	private validateAccessType() {
		const validateAccess = this.newUserAccess.niveauAcces !== undefined;
		if (!validateAccess) {
			this.errorAccess = 'emptyField';
			return false;
		}
		return true;
	}

	async addUserToCheptel() {
		this.errorUserId = null;
		this.errorAccess = null;
		if (this.validateAddUserToCheptel()) {
			if (isInputAnEmail(this.newUserAccess.userId)) {
				this.fetchUserIdByEmail(this.newUserAccess.userId).then((userId) => {
					this.newUserAccess.userId = userId;
					this.addUserAccessToExploitationDialog = false;

					this.apiService.exploitation_postUserAccess(this.newUserAccess).subscribe(
						() => {
							this.notificationService.notifySuccess('Utilisateur ajouté à ce cheptel'),
								this.addUserToCheptelList(this.newUserAccess.userId);
							this.listeUsersNiveauAccesDuCheptel.push(this.newUserAccess.niveauAcces);
							this.addUserExploitationIdToList(
								this.newUserAccess.userId,
								this.exploitation.numeroCheptel,
							);
							this.addUserAccessToExploitationDialog = false;
						},
						(err) => {
							this.notificationService.notifyError(err.statusText);
						},
					);
				});
			} else {
				this.addUserAccessToExploitationDialog = false;

				this.apiService.exploitation_postUserAccess(this.newUserAccess).subscribe(
					() => {
						this.notificationService.notifySuccess('Utilisateur ajouté à ce cheptel'),
							this.updateExploitationPage();
						this.addUserAccessToExploitationDialog = false;
					},
					(err) => {
						this.notificationService.notifyError(err.statusText);
					},
				);
			}
		}
	}

	editPermissions() {
		this.permissionEditionDialog = true;
	}

	hideAllDialog() {
		this.permissionEditionDialog = false;
		this.addUserAccessToExploitationDialog = false;
		this.editUserAccessToExploitationDialog = false;
		this.editFtpDialog = false;
	}

	openEditFtpDialog() {
		this.editFtpDialog = true;
		this.showPassword = false;
		this.resetFtpCheckStatus();
	}

	resetFtpCheckStatus() {
		this.ftpCheckStatus = 'idle';
	}

	checkFtpCredentials() {
		this.ftpCheckStatus = 'checking';
		this.apiService
			.exploitation_checkCredentialsEdeFtp(this.exploitation.numeroCheptel, {
				username: this.proprietesExploitation.identifiantsEdeFtp.utilisateur,
				password: this.proprietesExploitation.identifiantsEdeFtp.motDePasse,
			})
			.subscribe(
				(response) => {
					this.ftpCheckStatus = response.valid ? 'valid' : 'invalid';
					this.startFtpCheckCooldown();
				},
				(err) => {
					this.ftpCheckStatus = 'invalid';
					this.notificationService.notifyError(err.statusText);
					this.startFtpCheckCooldown();
				},
			);
	}

	private startFtpCheckCooldown() {
		this.ftpCheckCooldown = true;
		this.ftpCheckCooldownTimeout = setTimeout(() => {
			this.ftpCheckCooldown = false;
		}, FTP_CHECK_COOLDOWN_MS);
	}

	saveFtpCredentials() {
		this.apiService
			.exploitation_postCredentialsEdeFtp(this.exploitation.numeroCheptel, {
				username: this.proprietesExploitation.identifiantsEdeFtp.utilisateur,
				password: this.proprietesExploitation.identifiantsEdeFtp.motDePasse,
			})
			.subscribe(
				() => {
					this.notificationService.notifySuccess('Identifiants FTP mis à jour');
					this.editFtpDialog = false;
					this.updateExploitationPage();
				},
				(err) => {
					this.notificationService.notifyError(err.statusText);
				},
			);
	}

	togglePasswordVisibility() {
		this.showPassword = !this.showPassword;
	}

	savePermission() {
		const permission = {
			mouvement: this.proprietesExploitation.mouvement,
			offreAbonnement: this.proprietesExploitation.offreAbonnement,
			featureDisplayAlertes: this.proprietesExploitation.featureDisplayAlertes,
			featureDisplayEquarrissage: this.proprietesExploitation.featureDisplayEquarrissage,
			featureCommandeBoucles: this.proprietesExploitation.featureCommandeBoucles,
			featureUpdateLots: this.proprietesExploitation.featureUpdateLots,
			featureSaisiePesee: this.proprietesExploitation.featureSaisiePesee,
			featureSaisieReproduction: this.proprietesExploitation.featureSaisieReproduction,
			featureSaisieSanitaire: this.proprietesExploitation.featureSaisieSanitaire,
			type: this.proprietesExploitation.type,
		};
		this.apiService.exploitation_putAdminExploitation(this.exploitation.numeroCheptel, permission).subscribe(
			() => {
				this.notificationService.notifySuccess('Mise à jour réussie');
				this.updateExploitationPage();
			},
			(err) => {
				this.notificationService.notifyError(err.statusText);
			},
		);

		this.permissionEditionDialog = false;
	}

	editUserAccessToExploitation(user: GetUserResponse, userExploitationId: string, userNiveauAcces: string) {
		this.editUserAccessToExploitationDialog = true;
		this.currentUser = user;
		this.currentUserExploitationId = userExploitationId;
		this.currentNiveauAcces = userNiveauAcces;
	}

	saveNewUserAccess() {
		this.apiService.exploitation_putUserAccess(this.currentUserExploitationId, this.currentNiveauAcces).subscribe(
			() => {
				this.notificationService.notifySuccess('Droit utilisateur mis à jour');
				this.updateNiveauAccessListe(this.currentNiveauAcces, this.currentUserExploitationId);
				this.updateExploitationPage();
			},
			(err) => {
				this.notificationService.notifyError(err.statusText);
			},
		);

		this.editUserAccessToExploitationDialog = false;
	}

	deleteUserAccess(user: GetUserResponse, userExploitationId: string) {
		this.confirmationService.confirm({
			message: "Êtes vous sûr de vouloir supprimer l'accès à ce cheptel pour " + user.email + ' ?',
			header: 'Confirmer',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.apiService.exploitation_deleteUserAccess(userExploitationId).subscribe(
					() => {
						this.notificationService.notifySuccess('Droit enlevé');
						const index = this.listeUsersDuCheptel.indexOf(user);
						this.listeUsersDuCheptel.splice(index, 1);
						this.listeUsersNiveauAccesDuCheptel.splice(index, 1);
						this.listeUserExploitationIds.splice(index, 1);
					},
					(err) => {
						this.notificationService.notifyError(err.statusText);
					},
				);
			},
		});
	}

	changeCheptelState() {
		this.confirmationService.confirm({
			message:
				this.exploitation.enabled === 'ENABLED'
					? 'Êtes vous sûr de vouloir désactiver le cheptel ' + this.exploitation.name + ' ?'
					: 'Êtes vous sûr de vouloir activer le cheptel ' + this.exploitation.name + ' ?',
			header: 'Confirmer',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				if (this.exploitation.enabled === 'ENABLED') {
					this.apiService.exploitation_putDown(this.exploitation.numeroCheptel).subscribe(
						() => {
							this.notificationService.notifySuccess('Cheptel désactivé');

							this.updateExploitationPage();
						},
						(err) => {
							this.notificationService.notifyError(err.statusText);
						},
					);
				} else {
					this.apiService.exploitation_putUp(this.exploitation.numeroCheptel).subscribe(
						() => {
							this.notificationService.notifySuccess('Cheptel activé');
							this.updateExploitationPage();
						},
						(err) => {
							this.notificationService.notifyError(err.statusText);
						},
					);
				}
			},
		});
	}

	changeSynchroState() {
		// La propriété synchronizationStatus n'existe plus
		// Cette fonctionnalité nécessite une refonte pour fonctionner sans cette propriété
		// Pour l'instant, on désactive toujours la synchronisation par défaut
		this.confirmationService.confirm({
			message:
				'Êtes vous sûr de vouloir désactiver la synchronisation sur le cheptel ' +
				this.exploitation.name +
				' ?',
			header: 'Confirmer',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.apiService.exploitation_putSyncDown(this.exploitation.numeroCheptel).subscribe(
					() => {
						this.notificationService.notifySuccess('Synchronisation désactivé'),
							this.updateExploitationPage();
					},
					(err) => {
						this.notificationService.notifyError(err.statusText);
					},
				);
			},
		});
	}

	syncExploitation() {
		this.confirmationService.confirm({
			message: 'Êtes vous sûr de vouloir relancer la synchronisation de ce Cheptel ?',
			header: 'Confirmer',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.syncExploitationState = true;
				this.apiService.syncExploitation(this.exploitation.numeroCheptel).subscribe(
					() => {
						this.syncExploitationState = false;
						this.notificationService.notifySuccess('Cheptel synchronisé avec succès');
					},
					(err) => {
						this.notificationService.notifyError(err.statusText);
						this.syncExploitationState = false;
					},
				);
			},
		});
	}

	syncAnimaux() {
		this.confirmationService.confirm({
			message: 'Êtes vous sûr de vouloir relancer la synchronisation des animaux de ce Cheptel ? ?',
			header: 'Confirmer',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.syncAnimauxState = true;
				this.apiService.syncAnimaux(this.exploitation.numeroCheptel).subscribe(
					() => {
						this.syncAnimauxState = false;
						this.notificationService.notifySuccess('Animaux synchronisés avec succès');
					},
					(err) => {
						this.notificationService.notifyError(err.statusText);
						this.syncAnimauxState = false;
					},
				);
			},
		});
	}

	fetchUserIdByEmail(email: string): Promise<string> {
		return new Promise((resolve, reject) => {
			this.apiService.exploitation_getUsers().subscribe(
				(response) => {
					for (const user in response.users) {
						const currentUser = response.users[user];
						if (currentUser.email === email) {
							resolve(currentUser.id);
							return;
						}
					}
					resolve('');
				},
				(error) => {
					reject(error.message);
				},
			);
		});
	}

	fetchUserList(exploitation: GetExploitationResponse) {
		const numeroCheptel = exploitation.numeroCheptel;
		this.isUserListLoading = true;
		this.apiService.exploitation_getUsersByCheptel(numeroCheptel).subscribe((response) => {
			this.users = response.users;
			for (const user in response.users) {
				const currentUser = response.users[user];
				this.userEmails.push(currentUser.email);
				for (const exploitation2 in currentUser.exploitations) {
					const currentExploitation = currentUser.exploitations[exploitation2];
					if (currentExploitation.numeroCheptel === exploitation.numeroCheptel) {
						this.listeUsersDuCheptel.push(currentUser);
						this.listeUsersNiveauAccesDuCheptel.push(currentExploitation.niveauAcces);
						this.listeUserExploitationIds.push(currentExploitation.userExploitationId);
					}
				}
			}
			this.isUserListLoading = false;
		});
	}

	fetchFtpCredentials() {
		this.apiService.exploitation_getCredentialsEdeFtp(this.exploitation.numeroCheptel).subscribe((credentials) => {
			this.proprietesExploitation.identifiantsEdeFtp = {
				utilisateur: credentials.username || '',
				motDePasse: credentials.password || '',
			};
		});
	}

	addUserToCheptelList(userId: string) {
		const addedUserToCheptel = this.users.find((user) => user.id === userId);
		this.listeUsersDuCheptel.push(addedUserToCheptel);
	}

	addUserExploitationIdToList(userId: string, exploitationId: string) {
		this.apiService.exploitation_getUser(userId).subscribe((user) => {
			const myUser = user;
			for (const exploitation2 in myUser.exploitations) {
				const currentExploitation = myUser.exploitations[exploitation2];
				if (currentExploitation.userExploitationId === exploitationId) {
					this.listeUserExploitationIds.push(currentExploitation.userExploitationId);
				}
			}
		});
	}

	updateNiveauAccessListe(niveauAcces: string, userExploitationId: string) {
		const index = this.listeUserExploitationIds.indexOf(userExploitationId);
		if (niveauAcces === 'SAISIE') {
			this.listeUsersNiveauAccesDuCheptel[index] = 'SAISIE';
		} else {
			this.listeUsersNiveauAccesDuCheptel[index] = 'CONSULTATION';
		}
	}

	updateExploitationPage() {
		this.apiService
			.exploitation_getAdminExploitation(this.route.snapshot.paramMap.get('id'))
			.subscribe((exploitation) => {
				this.exploitation = exploitation;
				this.proprietesExploitation = {
					mouvement: this.exploitation.mouvement,
					offreAbonnement: this.exploitation.offreAbonnement,
					featureDisplayAlertes: this.exploitation.featureDisplayAlertes,
					featureDisplayEquarrissage: this.exploitation.featureDisplayEquarrissage,
					featureCommandeBoucles: this.exploitation.featureCommandeBoucles,
					featureUpdateLots: this.exploitation.featureUpdateLots,
					featureSaisiePesee: this.exploitation.featureSaisiePesee,
					featureSaisieReproduction: this.exploitation.featureSaisieReproduction,
					featureSaisieSanitaire: this.exploitation.featureSaisieSanitaire,
					type: this.exploitation.type,
					identifiantsEdeFtp: {
						utilisateur: '',
						motDePasse: '',
					},
				};
			});
		this.fetchFtpCredentials();
	}

	animalToDeleteNumeroNational = '';
	animalToDeleteNumeroNationalError = false;
	animalToDeleteCodePays = 'FR';
	animalToDeleteCodePaysError = false;

	deleteAnimal() {
		this.animalToDeleteNumeroNationalError = !/[0-9A-Z]{6,12}/.test(this.animalToDeleteNumeroNational);
		this.animalToDeleteCodePaysError = !/[A-Z]{2}/.test(this.animalToDeleteCodePays);

		if (this.animalToDeleteNumeroNationalError || this.animalToDeleteCodePaysError) {
			return;
		}

		this.apiService
			.get_animal_info(
				this.exploitation.numeroCheptel,
				this.exploitation.numeroCheptel,
				this.animalToDeleteNumeroNational,
				this.animalToDeleteCodePays,
			)
			.subscribe(
				(animalInfo) => {
					this.confirmationService.confirm({
						message:
							"Êtes-vous sûr de vouloir supprimer l'animal suivant ?\n\n" +
							`numero cheptel: ${this.exploitation.numeroCheptel}\n` +
							`numero national: ${animalInfo.numeroNational}\n` +
							`code pays: ${animalInfo.codePays}\n` +
							`nom: ${animalInfo.nom}\n` +
							`date naissance: ${animalInfo.dateNaissance}\n` +
							`date entree: ${animalInfo.dateEntree}\n`,
						header: 'Confirmer',
						icon: 'pi pi-exclamation-triangle',
						accept: () => {
							this.apiService
								.delete_animal_info(
									this.exploitation.numeroCheptel,
									this.exploitation.numeroCheptel,
									this.animalToDeleteNumeroNational,
									this.animalToDeleteCodePays,
								)
								.subscribe(
									() => {
										this.notificationService.notifySuccess('Animal supprimé');
										this.cleanDeleteAnimalForm();
									},
									(err) => {
										this.notificationService.notifyError(err.statusText);
									},
								);
						},
					});
				},
				(err) => {
					this.notificationService.notifyError(
						`Animal ${this.animalToDeleteNumeroNational} non trouvé dans le cheptel ${this.exploitation.numeroCheptel} : ` +
							err.statusText,
					);
				},
			);
	}

	cleanDeleteAnimalForm() {
		this.animalToDeleteNumeroNational = '';
		this.animalToDeleteNumeroNationalError = false;
		this.animalToDeleteCodePaysError = false;
	}

	hasProperty(propertyName: string): boolean {
		return this.exploitation && propertyName in this.exploitation && propertyName !== 'identifiantPrimaire';
	}

	protected readonly FeatureDisplayAlertes = FeatureDisplayAlertes;
	protected readonly FeatureDisplayEquarrissage = FeatureDisplayEquarrissage;
	protected readonly FeatureCommandeBoucles = FeatureCommandeBoucles;
	protected readonly FeatureSaisiePesee = FeatureSaisiePesee;
	protected readonly FeatureSaisieReproduction = FeatureSaisieReproduction;
	protected readonly FeatureSaisieSanitaire = FeatureSaisieSanitaire;
	protected readonly FeatureUpdateLots = FeatureUpdateLots;
	protected readonly FlagMouvement = FlagMouvement;
}
